'use client'

import { Button, LeftArrow } from '@desoutter/uikit'
import { ImageCarouselBlockFragment } from './ImageCarouselBlock.generated'
import { Image as DatoImage } from 'react-datocms'
import { useState, useRef, useEffect, useCallback } from 'react'
import { useThrottledResize } from '@/hooks/useThrottledResize'

type ImageCarouselBlockProps = {
	fragment: ImageCarouselBlockFragment
}

export default function ImageCarouselBlock({
	fragment,
}: ImageCarouselBlockProps) {
	const [curr, setCurr] = useState(0)
	const [imageWidths, setImageWidths] = useState<number[]>([])
	const containerRef = useRef<HTMLDivElement>(null)
	const imageRefs = useRef<(HTMLDivElement | null)[]>([])

	const prev = () =>
		setCurr((curr) => (curr === 0 ? fragment.images.length - 1 : curr - 1))

	const next = () =>
		setCurr((curr) => (curr === fragment.images.length - 1 ? 0 : curr + 1))

	const updateImageWidths = useCallback(() => {
		const widths = imageRefs.current.map((ref) => {
			if (ref) {
				const rect = ref.getBoundingClientRect()
				return rect.width
			}
			return 0
		})
		setImageWidths(widths)
	}, [])

	// Use throttled resize hook
	useThrottledResize(updateImageWidths, 100)

	// Calculer la largeur des images après le rendu
	useEffect(() => {
		// Attendre que les images soient chargées
		const timer = setTimeout(updateImageWidths, 100)

		return () => {
			clearTimeout(timer)
		}
	}, [fragment.images.length, updateImageWidths])

	// Calculer la translation basée sur la largeur effective des images
	const calculateTranslation = () => {
		if (imageWidths.length === 0) {
			return curr * 624
		}

		let translation = 0
		for (let i = 0; i < curr; i++) {
			translation += imageWidths[i] + 24 // 24px de gap
		}
		return translation
	}

	return (
		<div
			id={fragment.anchor ? 'spy_' + fragment.anchor.replaceAll(' ', '_') : ''}
			className={fragment.isDark ? 'dark' : ''}
		>
			<div className="bg-background text-foreground">
				<div className="container-auto py-16">
					<div className="flex flex-col w-full gap-6 overflow-hidden">
						<div className="flex justify-end gap-2">
							<Button
								title="previous_picture"
								color={fragment.isDark ? 'secondary' : 'tertiary'}
								className={'border border-gray-100 dark:border-gray-800'}
								icon={LeftArrow}
								onClick={prev}
							/>
							<Button
								title="next_picture"
								color={fragment.isDark ? 'secondary' : 'tertiary'}
								className={'border border-gray-100 dark:border-gray-800'}
								onClick={next}
							/>
						</div>

						<div className="relative" ref={containerRef}>
							<div
								className={
									'relative flex gap-6 transition-transform ease-out duration-500'
								}
								style={{
									transform: `translateX(-${calculateTranslation()}px)`,
								}}
							>
								{fragment.images.map((image, i) => (
									<div
										key={i}
										ref={(el) => (imageRefs.current[i] = el)}
										className="shrink-0 w-full sm:w-[600px]"
									>
										<DatoImage
											data={image.responsiveImage}
											className="w-full h-auto"
										/>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
